import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import type { Run, RunStep } from '@ankor-io/common/lang/backend/pipeline/types'
import type { ReportGenerationPipelineData } from '@ankor-io/common/types/reports/generated-report'

import { organizationKeys } from '@/queries/organization'

type SSEEvent = { run: Run<ReportGenerationPipelineData> } | { step: RunStep<string, Record<string, unknown>> }

/**
 * Subscribes to report generation run SSE when the run is in progress.
 * Updates React Query cache for run detail and run steps queries.
 */
export const useReportGenerationRunSSE = (
  orgId: string | undefined,
  jobId: string | undefined,
  runId: string | undefined,
  enabled: boolean,
) => {
  const queryClient = useQueryClient()
  const { getAccessToken } = useKindeAuth()

  useEffect(() => {
    if (!enabled || !orgId || !jobId || !runId) return

    const abortController = new AbortController()

    const connect = async () => {
      let token: string | undefined
      try {
        token = await getAccessToken()
      } catch {
        return
      }

      const url = `/api/lighthouse/organizations/${orgId}/generated-reports/jobs/${jobId}/runs/${runId}/listen`

      let response: Response
      try {
        response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        })
      } catch {
        return
      }

      if (!response.ok) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.reportGenJobRun(orgId, jobId, runId),
        })
        return
      }

      const contentType = response.headers.get('content-type') ?? ''

      if (contentType.includes('application/json')) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.reportGenJobRun(orgId, jobId, runId),
        })
        return
      }

      const reader = response.body?.getReader()
      if (!reader) return

      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (true) {
          const { value, done } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          const parts = buffer.split('\n\n')
          buffer = parts.pop() ?? ''

          for (const part of parts) {
            const dataLine = part.split('\n').find((line) => line.startsWith('data:'))
            if (!dataLine) continue

            const jsonStr = dataLine.slice(5).trim()
            if (!jsonStr) continue

            let event: SSEEvent
            try {
              event = JSON.parse(jsonStr)
            } catch {
              continue
            }

            if ('run' in event && event.run) {
              queryClient.setQueryData(organizationKeys.reportGenJobRun(orgId, jobId, runId), event.run)
            } else if ('step' in event && event.step) {
              const step = event.step
              const stepType = step.type

              const stepsKey = organizationKeys.reportGenRunSteps(orgId, jobId, runId, stepType)

              queryClient.setQueryData<RunStep<string, Record<string, unknown>>[]>(stepsKey, (old) => {
                if (!old) return [step]
                const idx = old.findIndex((s) => s.id === step.id)
                if (idx >= 0) {
                  const updated = [...old]
                  updated[idx] = step
                  return updated
                }
                return [...old, step]
              })
            }
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
      }

      queryClient.invalidateQueries({
        queryKey: organizationKeys.reportGenJobRun(orgId, jobId, runId),
      })
    }

    connect()

    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, jobId, runId, enabled])
}
